import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Link, useParams } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  Heading,
  LoadingOrbit,
  Page,
  PageHeader,
  Select,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { Gauge } from '@phosphor-icons/react';
import { shortSha } from '@chatsdet/types/perf';
import {
  formatHeadlineDelta,
  largestGatedDelta,
  runVerdictPresentation,
} from '../../../features/perf/perfPresentation';
import { formatRelativeTimeAgo } from '../../../features/workflows/useAgenticRunDetail';
import { usePerfRuns, usePerfTest, type PerfRunListItem } from '../../../features/perf/usePerf';
import { PerfDeltaChart, type PerfChartPoint } from './PerfDeltaChart';
import { RunNowDialog } from './RunNowDialog';

const COLUMNS: ColumnDef<StockFeatures, PerfRunListItem>[] = [
  {
    id: 'head',
    header: 'Head',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-mono">{shortSha(row.original.headSha)}</Text>,
    meta: { minWidth: 100 },
  },
  {
    id: 'base',
    header: 'Base',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-mono">{shortSha(row.original.baseSha)}</Text>,
    meta: { minWidth: 100 },
  },
  {
    id: 'verdict',
    header: 'Verdict',
    enableSorting: false,
    cell: ({ row }) => {
      const { label, color } = runVerdictPresentation(row.original);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 130 },
  },
  {
    id: 'delta',
    header: 'Largest gated delta',
    enableSorting: false,
    cell: ({ row }) => {
      const delta = largestGatedDelta(row.original.headlineDeltas);
      return delta ? (
        <Text size="sm" className="tabular-nums">{`${delta.label} ${formatHeadlineDelta(delta)}`}</Text>
      ) : (
        <Text size="sm" className="text-muted-foreground">—</Text>
      );
    },
    meta: { flex: 2, minWidth: 200 },
  },
  {
    id: 'started',
    header: 'Started',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">{formatRelativeTimeAgo(row.original.createdAt)}</Text>
    ),
    meta: { minWidth: 110 },
  },
];

export function PerfTestPage() {
  const { testId } = useParams({ from: '/performance/tests/$testId' });
  const test = usePerfTest(testId);
  const runs = usePerfRuns({ perfTestId: testId });
  const [metricId, setMetricId] = React.useState<string | null>(null);

  const items = runs.data?.pages.flatMap((p) => p.items) ?? [];
  const completed = items.filter((run) => run.status === 'completed');

  // Metrics worth charting are the gated ones; labels come from the newest run that has them.
  const metrics = new Map<string, string>();
  for (const run of completed) for (const d of run.headlineDeltas) if (!metrics.has(d.id)) metrics.set(d.id, d.label);
  const selected = metricId && metrics.has(metricId) ? metricId : (metrics.keys().next().value ?? null);

  // Newest first, straight from the list: headline deltas carry the median and its CI.
  const points: PerfChartPoint[] = completed.flatMap((run) => {
    const delta = run.headlineDeltas.find((d) => d.id === selected);
    return delta ? [{ runId: run.id, headSha: run.headSha, delta }] : [];
  });

  return (
    <Page>
      <PageHeader
        icon={<Gauge size={24} weight="fill" />}
        title={test.data?.name ?? 'Performance test'}
        description={test.data ? `Environment: ${test.data.environment}` : undefined}
        actions={<RunNowDialog testId={testId} />}
      />
      <Link to="/performance" className="text-sm font-medium text-foreground hover:underline">
        All performance tests
      </Link>
      {runs.isError ? (
        <Banner variant="error">{runs.error.message}</Banner>
      ) : runs.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Gauge size={48} />}
          title="No runs yet"
          description="Use Run now to measure a version against the deployed one."
        />
      ) : (
        <>
          <Card>
            <CardContent>
              <Heading level={3}>Median delta per run</Heading>
              <Text size="sm" className="text-muted-foreground">
                Point is the median delta, bar its 95% confidence interval, shaded band the 10% minimum effect.
                For throughput (rps) above zero is faster; for every other metric above zero is slower.
              </Text>
              {metrics.size > 0 && (
                <Select
                  label="Metric"
                  items={[...metrics].map(([id, label]) => ({ id, label }))}
                  selectedKey={selected}
                  onSelectionChange={(key) => setMetricId(String(key))}
                  className="max-w-xs"
                />
              )}
              {points.length > 0 ? (
                <PerfDeltaChart points={points} />
              ) : (
                <Text size="sm" className="text-muted-foreground">No completed runs measured this metric.</Text>
              )}
            </CardContent>
          </Card>
          <DataTable
            columns={COLUMNS}
            data={items}
            aria-label="Run history table"
            getRowId={(row) => row.id}
            getRowHref={(row) => `/performance/runs/${row.id}`}
          />
          {runs.hasNextPage && (
            <div className="flex justify-center py-4">
              <Button variant="outline" onPress={() => runs.fetchNextPage()} isDisabled={runs.isFetchingNextPage}>
                {runs.isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
